// Упаковочный лист — форма генерации PDF.  Открывается из карточки заказа
// (?manufacturing_order_id=:id): бэкенд авто-раскладывает изделия на паллеты
// (GET /admin/packing-lists/prefill), оператор правит шапку и строки, жмёт
// «Сформировать» → POST /admin/packing-lists/render возвращает PDF, который
// открывается в новой вкладке (как протокол испытаний).

import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { request } from "@/lib/api";

const INPUT =
  "w-full px-3 py-2 rounded-md bg-surface border border-border text-sm";
const INPUT_MONO = `${INPUT} font-mono`;
const CELL = "w-full px-1.5 py-1 rounded bg-surface border border-border text-sm font-mono text-right";
const CELL_TXT = "w-full px-1.5 py-1 rounded bg-surface border border-border text-sm";

const ACCESS_KEY = "mes:auth:access";

interface Constants {
  capacity: number;
  netKgPerElement: number;
  tareKg: number;
  footprintL: number;
  footprintW: number;
  itemsPerLayer: number;
  baseHeightCm: number;
  layerHeightCm: number;
}

interface PrefillRow {
  index: number;
  description: string;
  model: string;
  qty: number;
  pallet_no: number;
  l: number;
  w: number;
  h: number;
  net: number;
  gross: number;
  volume: number;
}

interface Prefill {
  meta: { supplier: string; customer: string; invoice_no: string; doc_date: string };
  rows: PrefillRow[];
  totals: { qty: number; net: number; gross: number; volume: number; pallets: number };
  constants: Constants;
}

// Строки формы держим строками — свободный ввод чисел; деривативы (нетто/брутто/
// объём) считаем на лету из констант.
interface RowForm {
  description: string;
  model: string;
  qty: string;
  l: string;
  w: string;
  h: string;
}

// До ответа prefill — нули; реальные значения приходят из настроек и
// описания упаковки техпроцесса.
const EMPTY_CONSTANTS: Constants = {
  capacity: 0,
  netKgPerElement: 0,
  tareKg: 0,
  footprintL: 0,
  footprintW: 0,
  itemsPerLayer: 0,
  baseHeightCm: 0,
  layerHeightCm: 0,
};

function todayRu(): string {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${d.getFullYear()}`;
}

const n = (s: string): number => {
  const x = Number(s);
  return Number.isFinite(x) ? x : 0;
};
const round2 = (x: number): number => Math.round(x * 100) / 100;

function heightFor(qty: number, c: Constants): number {
  const layers = Math.max(1, Math.ceil(qty / (c.itemsPerLayer || 1)));
  return c.baseHeightCm + c.layerHeightCm * layers;
}

function deriveRow(r: RowForm, c: Constants) {
  const qty = n(r.qty);
  const net = round2(qty * c.netKgPerElement);
  const gross = round2(net + c.tareKg);
  const volume = round2((n(r.l) * n(r.w) * n(r.h)) / 1_000_000);
  return { net, gross, volume };
}

function blankRow(c: Constants, model: string, description: string): RowForm {
  return {
    description,
    model,
    qty: "",
    l: String(c.footprintL),
    w: String(c.footprintW),
    h: String(heightFor(0, c)),
  };
}

export default function PackingListForm() {
  return (
    <RequirePermission codes="print.use">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const [params] = useSearchParams();
  const moId = params.get("manufacturing_order_id");

  const [supplier, setSupplier] = useState("");
  const [customer, setCustomer] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [docDate, setDocDate] = useState(todayRu());
  const [rows, setRows] = useState<RowForm[]>([]);
  const [constants, setConstants] = useState<Constants>(EMPTY_CONSTANTS);
  const [hydrated, setHydrated] = useState(false);

  const prefill = useQuery({
    queryKey: ["admin", "packing-lists", "prefill", moId],
    queryFn: () =>
      request<Prefill>(`/admin/packing-lists/prefill${moId ? `?manufacturing_order_id=${moId}` : ""}`),
  });

  useEffect(() => {
    if (!prefill.data || hydrated) return;
    const d = prefill.data;
    setConstants(d.constants);
    if (d.meta.supplier) setSupplier(d.meta.supplier);
    // Пустая раскладка (нет заказа или количества) — одна строка для ручного ввода.
    setRows(
      d.rows.length > 0
        ? d.rows.map((r) => ({
            description: r.description,
            model: r.model,
            qty: String(r.qty),
            l: String(r.l),
            w: String(r.w),
            h: String(r.h),
          }))
        : [blankRow(d.constants, "", "")],
    );
    setHydrated(true);
  }, [prefill.data, hydrated]);

  function patchRow(i: number, k: keyof RowForm, v: string) {
    setRows((cur) => {
      const next = cur.slice();
      const row = { ...next[i], [k]: v };
      // Меняя кол-во — пересчитываем высоту по слоям (оператор может переписать H).
      if (k === "qty") row.h = String(heightFor(n(v), constants));
      next[i] = row;
      return next;
    });
  }

  function addRow() {
    const model = rows[0]?.model ?? "";
    const desc = rows[0]?.description ?? "";
    setRows((cur) => [...cur, blankRow(constants, model, desc)]);
  }

  function removeRow(i: number) {
    setRows((cur) => cur.filter((_, idx) => idx !== i));
  }

  // Авто-раскладка из общего количества (по ёмкости): перезаполняет таблицу.
  function autoSplit(total: number) {
    const model = rows[0]?.model ?? "";
    const desc = rows[0]?.description ?? "";
    const out: RowForm[] = [];
    let remaining = Math.max(0, Math.floor(total));
    const cap = constants.capacity > 0 ? constants.capacity : 1;
    while (remaining > 0) {
      const qty = Math.min(cap, remaining);
      out.push({
        description: desc,
        model,
        qty: String(qty),
        l: String(constants.footprintL),
        w: String(constants.footprintW),
        h: String(heightFor(qty, constants)),
      });
      remaining -= qty;
    }
    if (out.length > 0) setRows(out);
  }

  const totals = rows.reduce(
    (acc, r) => {
      const d = deriveRow(r, constants);
      acc.qty += n(r.qty);
      acc.net = round2(acc.net + d.net);
      acc.gross = round2(acc.gross + d.gross);
      acc.volume = round2(acc.volume + d.volume);
      return acc;
    },
    { qty: 0, net: 0, gross: 0, volume: 0 },
  );

  const m = useMutation({
    mutationFn: async (): Promise<void> => {
      const payloadRows = rows
        .filter((r) => n(r.qty) > 0)
        .map((r, i) => {
          const d = deriveRow(r, constants);
          return {
            index: i + 1,
            description: r.description.trim(),
            model: r.model.trim(),
            qty: n(r.qty),
            pallet_no: i + 1,
            l: n(r.l),
            w: n(r.w),
            h: n(r.h),
            net: d.net,
            gross: d.gross,
            volume: d.volume,
          };
        });
      if (payloadRows.length === 0)
        throw new Error("Добавьте хотя бы один паллет с количеством > 0");

      const body = {
        meta: {
          supplier: supplier.trim(),
          customer: customer.trim(),
          invoice_no: invoiceNo.trim(),
          doc_date: docDate.trim(),
        },
        rows: payloadRows,
      };

      const token =
        typeof localStorage !== "undefined" ? localStorage.getItem(ACCESS_KEY) : null;
      const res = await fetch("/api/v1/admin/packing-lists/render", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          accept: "application/pdf",
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const text = await res.text();
          const j = text ? JSON.parse(text) : null;
          msg = j?.error?.message ?? text ?? msg;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });

  if (moId && !hydrated && prefill.isLoading) {
    return <p className="text-text-muted">Загружаю данные заказа…</p>;
  }

  return (
    <>
      <PageHeader
        eyebrow="Печать · документы"
        title="Упаковочный лист"
        description="Авто-раскладка на паллеты из заказа. Проверьте получателя, накладную и строки, затем «Сформировать»."
      />

      {prefill.isError && (
        <ErrorBanner>
          Не удалось загрузить данные заказа: {(prefill.error as Error).message}
        </ErrorBanner>
      )}

      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
      >
        {/* Шапка */}
        <fieldset className="card p-4 space-y-3">
          <legend className="text-xs font-mono uppercase tracking-wider text-text-muted px-2">
            Шапка
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Поставщик">
              <textarea rows={4} className={INPUT} value={supplier} onChange={(e) => setSupplier(e.target.value)} />
            </Field>
            <Field label="Покупатель">
              <textarea rows={4} className={INPUT} value={customer} onChange={(e) => setCustomer(e.target.value)} />
            </Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Накладная №">
              <input className={INPUT_MONO} value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="14" />
            </Field>
            <Field label="Дата">
              <input className={INPUT_MONO} value={docDate} onChange={(e) => setDocDate(e.target.value)} placeholder="дд.мм.гггг" />
            </Field>
          </div>
        </fieldset>

        {/* Паллеты */}
        <fieldset className="card p-4 space-y-3">
          <legend className="text-xs font-mono uppercase tracking-wider text-text-muted px-2">
            Паллеты
          </legend>

          <div className="flex flex-wrap items-end gap-2">
            <AutoSplit onSplit={autoSplit} />
            <button type="button" className="btn" onClick={addRow}>
              + Паллет
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-text-muted text-xs">
                  <Th>№</Th>
                  <Th className="text-left">Описание</Th>
                  <Th className="text-left">Модель</Th>
                  <Th>кол-во</Th>
                  <Th>L</Th>
                  <Th>W</Th>
                  <Th>H</Th>
                  <Th>Нетто</Th>
                  <Th>Брутто</Th>
                  <Th>Объём</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const d = deriveRow(r, constants);
                  return (
                    <tr key={i} className="border-t border-border">
                      <td className="px-1 text-center text-text-muted">{i + 1}</td>
                      <td className="px-1"><input className={CELL_TXT} value={r.description} onChange={(e) => patchRow(i, "description", e.target.value)} /></td>
                      <td className="px-1"><input className={CELL_TXT + " font-mono"} value={r.model} onChange={(e) => patchRow(i, "model", e.target.value)} /></td>
                      <td className="px-1 w-16"><input type="number" step="any" className={CELL} value={r.qty} onChange={(e) => patchRow(i, "qty", e.target.value)} /></td>
                      <td className="px-1 w-14"><input type="number" step="any" className={CELL} value={r.l} onChange={(e) => patchRow(i, "l", e.target.value)} /></td>
                      <td className="px-1 w-14"><input type="number" step="any" className={CELL} value={r.w} onChange={(e) => patchRow(i, "w", e.target.value)} /></td>
                      <td className="px-1 w-14"><input type="number" step="any" className={CELL} value={r.h} onChange={(e) => patchRow(i, "h", e.target.value)} /></td>
                      <td className="px-1 text-right font-mono text-text-muted">{d.net.toFixed(2)}</td>
                      <td className="px-1 text-right font-mono text-text-muted">{d.gross.toFixed(2)}</td>
                      <td className="px-1 text-right font-mono text-text-muted">{d.volume.toFixed(2)}</td>
                      <td className="px-1 text-center">
                        <button type="button" className="text-danger" title="Удалить паллет" onClick={() => removeRow(i)}>✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border font-semibold">
                  <td className="px-1" colSpan={3}>Итого</td>
                  <td className="px-1 text-right font-mono">{totals.qty}</td>
                  <td colSpan={3}></td>
                  <td className="px-1 text-right font-mono">{totals.net.toFixed(2)}</td>
                  <td className="px-1 text-right font-mono">{totals.gross.toFixed(2)}</td>
                  <td className="px-1 text-right font-mono">{totals.volume.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="text-xs text-text-muted">
            Паллетов: {rows.length}. Нетто/брутто/объём считаются автоматически
            (нетто = кол-во × {constants.netKgPerElement} кг, брутто = нетто + {constants.tareKg} кг,
            объём = L·W·H / 1 000 000). Размеры и высоту можно править вручную.
          </p>
        </fieldset>

        {m.isError && (
          <ErrorBanner onDismiss={() => m.reset()}>
            {(m.error as Error).message}
          </ErrorBanner>
        )}

        <div className="flex justify-end">
          <button type="submit" className="btn btn-primary" disabled={m.isPending}>
            {m.isPending ? "Формирую PDF…" : "Сформировать"}
          </button>
        </div>
      </form>
    </>
  );
}

function AutoSplit({ onSplit }: { onSplit: (total: number) => void }) {
  const [v, setV] = useState("");
  return (
    <div className="flex items-end gap-2">
      <Field label="Авто-раскладка из кол-ва">
        <input
          type="number"
          step="1"
          className={INPUT_MONO + " w-32"}
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder="напр. 276"
        />
      </Field>
      <button
        type="button"
        className="btn"
        onClick={() => {
          const total = Number(v);
          if (Number.isFinite(total) && total > 0) onSplit(total);
        }}
      >
        Разложить
      </button>
    </div>
  );
}

function Th({ children, className = "" }: { children?: React.ReactNode; className?: string }) {
  return <th className={`px-1 py-1 font-mono uppercase tracking-wide ${className}`}>{children}</th>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
